/**
 * AetherAuthGate
 * Wraps the Aether Dashboard and shows the login screen if user is not authenticated.
 * Provides the AetherAuthProvider context to children.
 */
import React, { useEffect, useState } from 'react';
import { AetherAuthProvider, useAetherAuth } from './AetherAuthContext';
import AetherLogin from './AetherLogin';
import { Loader2 } from 'lucide-react';
import PageMeta from '../../components/PageMeta';

/**
 * Anmeldung fuer die Desktop-App (David Media Installer).
 *
 * Die App kann kein eingebettetes Fenster benutzen — Google verweigert OAuth in
 * eingebetteten Browsern. Sie oeffnet deshalb diese Seite im echten Browser und
 * horcht solange auf einem Port auf 127.0.0.1. Nach erfolgreicher Anmeldung —
 * egal ob per Google oder per E-Mail-Code — reichen wir das Sitzungs-Token
 * genau dorthin zurueck.
 *
 * Absichtlich eng: nur 127.0.0.1, nur ein Port oberhalb 1024, und der state-Wert
 * muss derselbe sein, den die App erzeugt hat. Sonst passiert nichts.
 */
function appUebergabeZiel() {
  try {
    const p = new URL(window.location.href).searchParams;
    if (p.get('app') !== 'installer') return null;
    const port = parseInt(p.get('port'), 10);
    const state = p.get('state') || '';
    if (!(port > 1024 && port < 65536)) return null;
    if (!/^[a-f0-9]{8,64}$/i.test(state)) return null;
    return { port, state };
  } catch { return null; }
}

function Gate({ children }) {
  const { loading, isAuthenticated } = useAetherAuth();
  const [ziel] = useState(appUebergabeZiel);
  const [uebergeben, setUebergeben] = useState(false);

  useEffect(() => {
    if (!ziel || !isAuthenticated || uebergeben) return;
    const token = localStorage.getItem('aether_token');
    if (!token) return;
    setUebergeben(true);
    window.location.replace(
      `http://127.0.0.1:${ziel.port}/fertig?state=${encodeURIComponent(ziel.state)}` +
      `&token=${encodeURIComponent(token)}`
    );
  }, [ziel, isAuthenticated, uebergeben]);

  if (loading) {
    return (
      <div className="relative h-screen w-full flex items-center justify-center text-white" data-testid="aether-auth-loading">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={20} className="animate-spin text-white/40" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/30">
            Sitzung wird geprüft
          </span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AetherLogin />;
  }

  // Angemeldet und aus der App gekommen: hier endet der Weg im Browser.
  if (ziel) {
    return (
      <div className="relative h-screen w-full flex items-center justify-center text-white" data-testid="aether-app-handoff">
        <div className="flex flex-col items-center gap-3 text-center px-6">
          <Loader2 size={20} className="animate-spin text-white/40" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/30">
            Zurück zur App
          </span>
          <span className="text-sm text-white/50">
            Du bist angemeldet. Dieses Fenster kannst du schließen.
          </span>
        </div>
      </div>
    );
  }

  return children;
}

export default function AetherAuthGate({ children }) {
  return (
    <AetherAuthProvider>
      {/* Hard noindex on the entire Aether admin surface — robots.txt
          already blocks /aether*, this is the belt + suspenders so that
          even if a search engine ignores robots.txt or someone links
          directly to the path, the page itself signals 'do not index'. */}
      <PageMeta title="David Media Portal" noindex />
      <Gate>{children}</Gate>
    </AetherAuthProvider>
  );
}
